// Frontend types mirroring the PRR-48 Person/Worker API contract (PR #49).
//
// Worker and Person are the same record in this domain (see PRR-58). All fields use
// camelCase to match the backend Newtonsoft.Json serialisation (CamelCasePropertyNamesContractResolver).
// Dates: `dateOfBirth` is a date-only string ("1990-01-15"); `effectiveFrom`/`effectiveTo`
// are ISO-8601 UTC datetimes ("2025-01-01T00:00:00Z").

/** Generic API envelope wrapping every backend response. */
export interface ApiResponse<T> {
  success: boolean;
  statusCode: number;
  message?: string;
  result?: T;
}

// --- Reference data (from /api/person/{titles|genders|contact-types|address-types}) ---

/** Person status as returned/queried by the backend. */
export type PersonStatus = 'active' | 'terminated';

/** Gender codes accepted by the backend: M=Male, F=Female, I=Intersex, N=Not specified. */
export const GENDER_VALUES = ['M', 'F', 'I', 'N'] as const;
export type Gender = (typeof GENDER_VALUES)[number];

export const GENDER_LABELS: Record<Gender, string> = { M: 'Male', F: 'Female', I: 'Intersex', N: 'Not specified' };

/** Title values accepted by the backend (also fetched live from /api/person/titles). */
export const TITLE_VALUES = ['Mr', 'Mrs', 'Ms', 'Mx', 'Miss', 'Dr', 'Prof', 'Rev', 'Hon', 'Other'] as const;
export type Title = (typeof TITLE_VALUES)[number];

export const CONTACT_TYPE_VALUES = ['email_personal', 'email_work', 'mobile', 'landline', 'other'] as const;
export type ContactType = (typeof CONTACT_TYPE_VALUES)[number];

export const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  email_personal: 'Personal email',
  email_work: 'Work email',
  mobile: 'Mobile',
  landline: 'Landline',
  other: 'Other',
};

/** Contact types that hold an email address — relevant to BR-CT-001 (last-email retention). */
export const EMAIL_CONTACT_TYPES: Record<'email_personal' | 'email_work', string> = {
  email_personal: 'Personal email',
  email_work: 'Work email',
};

export type AddressType = 'residential' | 'postal';

export const ADDRESS_TYPE_VALUES: Record<AddressType, string> = {
  residential: 'Residential',
  postal: 'Postal',
};

// --- Person details ---

export interface PersonDetailsBase {
  legalFirstName?: string | null;
  legalMiddleName?: string | null;
  legalLastName?: string | null;
  preferredFirstName?: string | null;
  pronouns?: string | null;
  title?: string | null;
  /** Date-only string, e.g. "1990-01-15". Required by the backend, must be in the past. */
  dateOfBirth?: string | null;
  gender?: string | null;
  /** Optional JSON string of intelliHR custom fields. */
  intellihrCustomFields?: string | null;
  /** ISO-8601 UTC. Defaults to now server-side; retroactive (past) requires a reason. */
  effectiveFrom?: string | null;
  retroactiveReason?: string | null;
}

export interface CreatePersonRequest extends PersonDetailsBase {
  globalEmployeeId?: string | null;
}

export type UpdatePersonDetailRequest = PersonDetailsBase;

export interface PersonDetailResponse extends PersonDetailsBase {
  personId?: string;
  detailsId?: string;
  globalEmployeeId?: string | null;
  status?: PersonStatus;
  /** End of this version's validity; null = current. */
  effectiveTo?: string | null;
}

export interface PersonListParams {
  status?: PersonStatus;
  globalEmployeeId?: string;
  /** ISO-8601 UTC point-in-time read. */
  effectiveDate?: string;
  /** 0–100, backend default 20. */
  take?: number;
  /** >= 0. */
  skip?: number;
}

// --- Contacts ---

export interface PersonContactBase {
  contactType?: ContactType | string;
  contactValue?: string | null;
  isPrimary?: boolean;
  effectiveFrom?: string | null;
  retroactiveReason?: string | null;
}

export type CreatePersonContactRequest = PersonContactBase;

export interface UpdatePersonContactRequest {
  contactValue?: string | null;
  isPrimary?: boolean;
  effectiveFrom?: string | null;
  retroactiveReason?: string | null;
}

export interface PersonContactResponse extends PersonContactBase {
  contactId?: string;
  personId?: string;
  verified?: boolean;
  effectiveTo?: string | null;
}

// --- Addresses ---

export interface PersonAddressBase {
  addressLine1?: string | null;
  addressLine2?: string | null;
  suburb?: string | null;
  stateOrRegion?: string | null;
  postcode?: string | null;
  /** 2–3 chars; defaults to "AU" server-side. */
  countryCode?: string | null;
  effectiveFrom?: string | null;
  retroactiveReason?: string | null;
}

export interface CreatePersonAddressRequest extends PersonAddressBase {
  addressType?: AddressType | string;
}

export type UpdatePersonAddressRequest = PersonAddressBase;

export interface PersonAddressResponse extends PersonAddressBase {
  addressId?: string;
  personId?: string;
  addressType?: AddressType | string;
  effectiveTo?: string | null;
}

// --- Emergency contacts (read-only; HR-mastered, BR-EC-002) ---

export interface PersonEmergencyContactResponse {
  emergencyContactId?: string;
  personId?: string;
  contactName?: string | null;
  relationship?: string | null;
  primaryPhone?: string | null;
  secondaryPhone?: string | null;
  email?: string | null;
  address?: string | null;
  priorityOrder?: number;
  hrSourceId?: string | null;
  /** ISO-8601 UTC of the last HR sync. */
  lastSyncedAt?: string | null;
}
